/**
 * Date utility functions for consistent date handling across the app
 */

import { Timestamp } from 'firebase/firestore';

// Month name to number mapping (0-based for JavaScript Date)
const MONTH_MAP = {
  'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5, 
  'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
};

// Number to month name mapping
const MONTH_NAMES = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

/**
 * Convert legacy event date format to the new standardized format
 * @param {Object} legacyDate - The old date format with month and day properties
 * @returns {string} - Date in dd/MMM/yy format
 */
export const convertLegacyDateToStandard = (legacyDate) => {
  if (!legacyDate || !legacyDate.month || !legacyDate.day) {
    // If any parts are missing, return current date as fallback
    const now = new Date();
    return formatDate(now);
  }

  // Default to current year if no year provided
  const year = legacyDate.year || new Date().getFullYear();
  
  // Get month number from abbreviation
  const monthNum = MONTH_MAP[legacyDate.month.toUpperCase()] || 0;
  
  // Parse day as number (default to 1 if invalid)
  const day = parseInt(legacyDate.day, 10) || 1;
  
  // Create date object and format it
  const date = new Date(year, monthNum, day);
  return formatDate(date);
};

/**
 * Format a Date object into dd/MMM/yy format
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  // Handle invalid date objects
  if (!(date instanceof Date) || isNaN(date)) {
    console.warn("Invalid date passed to formatDate", date);
    date = new Date(); // Fallback to current date
  }
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  
  return `${day}/${month}/${year}`;
};

/**
 * Parse a date string in dd/MMM/yy format into a Date object
 * @param {string} dateStr - Date string in dd/MMM/yy format
 * @returns {Date} - JavaScript Date object
 */
export const parseDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') {
    console.warn("Invalid dateStr passed to parseDate", dateStr);
    return new Date(); // Return current date as fallback
  }
  
  // Parse "dd/MMM/yy" format
  const parts = dateStr.split('/');
  
  if (parts.length !== 3) {
    console.warn("Invalid format in parseDate", dateStr);
    return new Date(); // Invalid format, return current date
  }
  
  const day = parseInt(parts[0], 10);
  const monthStr = parts[1].toUpperCase();
  const year = parseInt(parts[2], 10);
  
  // Handle 2-digit year (assume 20xx for simplicity)
  const fullYear = year < 100 ? 2000 + year : year;
  
  // Get month number from name
  const monthNum = MONTH_MAP[monthStr] || 0;
  
  return new Date(fullYear, monthNum, day);
};

/**
 * Extract month abbreviation from a date string
 * @param {string} dateStr - Date string in dd/MMM/yy format
 * @returns {string} - Month abbreviation (e.g., "JAN")
 */
export const getMonthFromDateString = (dateStr) => {
  if (!dateStr) return '';
  
  const parts = dateStr.split('/');
  return parts.length >= 2 ? parts[1].toUpperCase() : '';
};

/**
 * Get current date in dd/MMM/yy format
 * @returns {string} - Current date formatted as dd/MMM/yy
 */
export const getCurrentDateFormatted = () => {
  return formatDate(new Date());
};

/**
 * Compare two dates for sorting (most recent first)
 * @param {string} dateA - First date in dd/MMM/yy format
 * @param {string} dateB - Second date in dd/MMM/yy format
 * @returns {number} - Sort order (-1, 0, 1)
 */
export const compareDatesDesc = (dateA, dateB) => {
  const dateObjA = parseDate(dateA);
  const dateObjB = parseDate(dateB);
  
  return dateObjB - dateObjA; // Most recent first
};

/**
 * Calendar day at local midnight for an event (for comparing to "today").
 * Uses dateStr or legacy event.date. Returns null if not parseable.
 * @param {Object} event - Event object
 * @returns {Date|null}
 */
export const getEventCalendarDay = (event) => {
  if (!event) return null;
  let dateStr = event.dateStr;
  if (!dateStr && event.date && event.date.month && event.date.day) {
    dateStr = convertLegacyDateToStandard(event.date);
  }
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const d = parseDate(dateStr);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Stable key for a calendar day: "YYYY-MM-DD".
 * @param {Date} date
 * @returns {string}
 */
export const calendarDayKey = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Homepage card fetch: days loaded ahead of the user's position. */
export const EVENT_LOAD_DAY_WINDOW = 5;

/** First public list window. Long enough for the Sarajevo events page to be a month, not a strip. */
export const EVENT_SEO_LIST_DAYS = 28;

/**
 * Local midnight for a date (year/month/day only).
 * @param {Date} [date]
 * @returns {Date}
 */
export const startOfLocalDay = (date = new Date()) => {
  const src = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return new Date(src.getFullYear(), src.getMonth(), src.getDate());
};

/**
 * Add whole calendar days to a local date (does not mutate).
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export const addCalendarDays = (date, days) => {
  const start = startOfLocalDay(date);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + (days || 0));
};

/**
 * True when aggregate.byDay has any key on or after the given local day.
 * @param {Record<string, number>|null|undefined} byDay
 * @param {Date} day
 * @returns {boolean}
 */
export const hasAggregateDaysAtOrAfter = (byDay, day) => {
  const key = calendarDayKey(startOfLocalDay(day));
  if (!key || !byDay || typeof byDay !== 'object') return false;
  return Object.keys(byDay).some((dayKey) => dayKey >= key);
};

/**
 * Whether two dates fall on the same local calendar day.
 * @param {Date|null} a
 * @param {Date|null} b
 * @returns {boolean}
 */
export const isSameCalendarDay = (a, b) => {
  if (!a || !b) return false;
  return calendarDayKey(a) === calendarDayKey(b);
};

/**
 * Public day separator label, e.g. "12.AUGUST - WEDNESDAY".
 * @param {Date} date
 * @returns {string}
 */
export const formatEventDaySeparator = (date) => {
  if (!date || isNaN(date.getTime())) return 'Unknown date';
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toUpperCase();
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleDateString('en-GB', { month: 'long' }).toUpperCase();
  return `${day}.${month} - ${weekday}`;
};

/**
 * Count events per calendar day.
 * @param {Array<Record<string, unknown>>} events
 * @returns {Record<string, number>}
 */
export const countEventsByCalendarDay = (events) => {
  const counts = {};
  if (!Array.isArray(events)) return counts;
  for (const event of events) {
    const day = getEventCalendarDay(event);
    if (!day) continue;
    const key = calendarDayKey(day);
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
};

/**
 * Group events by calendar day, sorted ascending. Undated events go last.
 * @param {Array<Record<string, unknown>>} events
 * @returns {Array<{ day: Date|null, label: string, events: Array<Record<string, unknown>> }>}
 */
export const groupEventsByCalendarDay = (events) => {
  if (!Array.isArray(events) || events.length === 0) return [];

  const byDay = new Map();
  const undated = [];

  for (const event of events) {
    const day = getEventCalendarDay(event);
    if (!day) {
      undated.push(event);
      continue;
    }
    const key = day.getTime();
    if (!byDay.has(key)) {
      byDay.set(key, {
        day,
        label: formatEventDaySeparator(day),
        events: [],
      });
    }
    byDay.get(key).events.push(event);
  }

  const groups = Array.from(byDay.values()).sort(
    (a, b) => a.day.getTime() - b.day.getTime()
  );

  if (undated.length > 0) {
    groups.push({ day: null, label: 'Unknown date', events: undated });
  }

  return groups;
};

/**
 * Firestore Timestamp at local midnight for event start (queryable field).
 * @param {Object} event
 * @returns {import('firebase/firestore').Timestamp|null}
 */
export const eventStartAtFromEvent = (event) => {
  const day = getEventCalendarDay(event);
  return day ? Timestamp.fromDate(day) : null;
};

/**
 * Comparator: upcoming events first (soonest date first), then past events
 * (most recent past first). Events without a parseable date sort last among past.
 * @param {Object} a
 * @param {Object} b
 * @param {Date} [now]
 * @returns {number}
 */
export const compareEventsUpcomingThenPast = (a, b, now = new Date()) => {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayA = getEventCalendarDay(a);
  const dayB = getEventCalendarDay(b);

  const isPastDay = (day) => !day || day < startOfToday;
  const aPast = isPastDay(dayA);
  const bPast = isPastDay(dayB);

  if (aPast !== bPast) {
    return aPast ? 1 : -1;
  }
  if (!aPast && !bPast) {
    return dayA.getTime() - dayB.getTime();
  }
  if (dayA && dayB) {
    return dayB.getTime() - dayA.getTime();
  }
  if (!dayA && !dayB) return 0;
  if (!dayA) return 1;
  if (!dayB) return -1;
  return 0;
};

/**
 * Sort a copy: upcoming first (soonest first), then past (recent first).
 * @param {Array} events
 * @param {Date} [now]
 * @returns {Array}
 */
export const sortEventsUpcomingThenPast = (events, now = new Date()) =>
  [...events].sort((x, y) => compareEventsUpcomingThenPast(x, y, now));

/**
 * Parse date, time, and location from the description text pattern
 * @param {string} description - Description text with date/time/location info
 * @returns {Object} - Extracted date, time, location and cleaned description
 */
export const parseEventDetailsFromDescription = (description) => {
  if (!description) {
    return {
      dateStr: null,
      time: null,
      location: null,
      cleanDescription: description
    };
  }
  
  // Match pattern: "Date @ Time LOCATION - rest of description"
  // e.g. "28 Aprila @ 14:30 – 16:00 MUZEJ KNJIŽEVNOSTI ; rest of description"
  const regex = /^(\d{1,2}\s+\w+)\s*@\s*([\d:–\-\s]+)\s+([A-Z\s]+(?:\s+–\s+[^;()]+)?)[;(]?\s*(.*)/s;
  const match = description.match(regex);
  
  if (!match) {
    return {
      dateStr: null,
      time: null,
      location: null,
      cleanDescription: description
    };
  }
  
  // Extract date, time and location from match
  const [dateRaw, timeRaw, locationRaw, restOfDescription] = match.slice(1);
  
  // Parse date (convert months from local language to English abbreviation)
  let dateStr = null;
  const monthMap = {
    'januar': 'JAN', 'januara': 'JAN', 'januaru': 'JAN',
    'februar': 'FEB', 'februara': 'FEB', 'februaru': 'FEB',
    'mart': 'MAR', 'marta': 'MAR', 'martu': 'MAR',
    'april': 'APR', 'aprila': 'APR', 'aprilu': 'APR',
    'maj': 'MAY', 'maja': 'MAY', 'maju': 'MAY',
    'jun': 'JUN', 'juna': 'JUN', 'junu': 'JUN', 'juni': 'JUN',
    'jul': 'JUL', 'jula': 'JUL', 'julu': 'JUL', 'juli': 'JUL',
    'avgust': 'AUG', 'avgusta': 'AUG', 'avgustu': 'AUG',
    'septembar': 'SEP', 'septembra': 'SEP', 'septembru': 'SEP',
    'oktobar': 'OCT', 'oktobra': 'OCT', 'oktobru': 'OCT',
    'novembar': 'NOV', 'novembra': 'NOV', 'novembru': 'NOV',
    'decembar': 'DEC', 'decembra': 'DEC', 'decembru': 'DEC'
  };
  
  // Extract day and month
  const dateParts = dateRaw.trim().split(/\s+/);
  if (dateParts.length >= 2) {
    const day = parseInt(dateParts[0], 10);
    let monthText = dateParts[1].toLowerCase();
    
    // Find the English month abbreviation
    let monthAbbr = null;
    for (const [localMonth, englishAbbr] of Object.entries(monthMap)) {
      if (monthText.startsWith(localMonth)) {
        monthAbbr = englishAbbr;
        break;
      }
    }
    
    if (day && monthAbbr) {
      // Format in our standard dd/MMM/yy format
      const currentYear = new Date().getFullYear().toString().slice(-2);
      dateStr = `${day.toString().padStart(2, '0')}/${monthAbbr}/${currentYear}`;
    }
  }
  
  // Clean up location (remove any trailing descriptive text after the dash)
  let location = locationRaw.trim();
  // Extract just the venue name before any dash or special characters
  const locationMatch = location.match(/^([A-Z\s]+)(?:\s+–|\s+-)?/);
  if (locationMatch && locationMatch[1]) {
    location = locationMatch[1].trim();
  }
  
  return {
    dateStr,
    time: timeRaw.trim(),
    location,
    cleanDescription: restOfDescription.trim()
  };
}; 